import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const TYPE_LABELS = {
  title_card: "Title card",
  end_card: "End card",
  lower_third: "Lower third",
  logo_sting: "Logo sting",
  infographic: "Infographic",
  subtitles: "Subtitles",
  sticker: "Sticker",
};

const STATUS_CHIP = {
  draft: "bg-amber-500/20 text-amber-300",
  rendered: "bg-emerald-500/20 text-emerald-300",
};

/** MOTION ROOM — Stage 13.5 motion graphics module (M1 scaffold).
 *  Fable 5 (Claude Sonnet 4.6) writes Remotion compositions; we preview in
 *  the browser via @remotion/player and render headlessly on EC2. The TSX
 *  the model emits is statically validated by `motion_room_sandbox.py`
 *  BEFORE it touches a renderer. SCAFFOLD STATUS: backend live, TSX
 *  generation is stubbed (handwritten parameterized comp) until the
 *  emergentintegrations Fable 5 call lands. Param panel + render queue
 *  + Motion Bible are fully wired against the stub.
 */
export default function MotionRoomPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("brief");  // brief | preview
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [brief, setBrief] = useState("");
  const [mograph, setMograph] = useState(null);  // current asset
  const [paramsValues, setParamsValues] = useState({});
  const [type, setType] = useState("title_card");
  const [orientation, setOrientation] = useState("16:9");
  const [duration, setDuration] = useState(3.0);
  const [brandTag, setBrandTag] = useState("");
  const [busy, setBusy] = useState("");
  const inflight = React.useRef(false);

  useEffect(() => {
    axios.get(`${API}/api/grade-room/projects`, authHeaders())
      .then(({ data }) => setProjects(data.projects || []))
      .catch(() => toast.error("Could not load projects"));
  }, []);

  const generate = async () => {
    if (inflight.current) return;
    if (!projectId) { toast.error("Pick a project first"); return; }
    if (brief.trim().length < 4) { toast.error("Brief must be ≥4 chars"); return; }
    inflight.current = true;
    setBusy("generate");
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${projectId}/mograph/generate`,
        { brief: brief.trim(), type, orientation, duration_s: duration,
          brand_tag: brandTag.trim() || null },
        authHeaders());
      setMograph(data);
      setParamsValues(data.params_values || {});
      setStep("preview");
      toast.success(`Generated · ${TYPE_LABELS[data.type]}`
        + (data.codegen_stub ? " (stub — codegen wiring pending)" : ""));
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "MOTION_MODEL_UNAVAILABLE") {
        toast.error("Motion Room model unavailable", {
          duration: 8000,
          description: d.message || "Set MOTION_MODEL env to a supported Universal-Key model.",
        });
      } else if (d?.code === "EMERGENT_KEY_MISSING") {
        toast.error("EMERGENT_LLM_KEY missing in backend/.env");
      } else if (d?.code === "MOTION_TSX_FAILED_SANDBOX") {
        toast.error(`Codegen emitted unsafe TSX — sandbox blocked it · ${d.violations?.[0]?.reason}`);
      } else if (d?.code === "MOTION_OUTPUT_MALFORMED") {
        toast.error("Codegen returned unparseable output — retry");
      } else if (d?.code === "MOTION_TIMEOUT") {
        toast.error("Codegen timed out · retry");
      } else {
        toast.error(d?.message || d || "Generate failed");
      }
    }
    inflight.current = false;
    setBusy("");
  };

  const saveParams = useCallback(async (next) => {
    if (!mograph) return;
    try {
      await axios.put(`${API}/api/mograph/${mograph.id}/params`,
        { values: next }, authHeaders());
    } catch (e) {
      toast.error("Param save failed");
    }
  }, [mograph]);

  const renderAsset = async () => {
    if (!mograph || inflight.current) return;
    inflight.current = true;
    setBusy("render");
    try {
      const { data } = await axios.post(
        `${API}/api/mograph/${mograph.id}/render`, {}, authHeaders());
      toast.success(`Render queued · job ${data.job_id.slice(0, 8)}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "TSX_FAILED_SANDBOX") {
        toast.error(`Sandbox rejected · ${d.violations[0]?.reason}`);
      } else {
        toast.error(d?.message || d || "Render failed");
      }
    }
    inflight.current = false;
    setBusy("");
  };
  const schemaProps = mograph?.params_schema?.properties || {};
  const sortedProps = useMemo(() => Object.entries(schemaProps),
    [schemaProps]);

  return (
    <div className="min-h-screen bg-[#0c0a09] text-white/90 px-6 py-6"
      data-testid="motion-room-page">
      <header className="flex items-center gap-4 pb-5 border-b border-white/10 mb-6">
        <button onClick={() => step === "brief" ? navigate("/recipes")
          : setStep("brief")}
          className="text-white/40 hover:text-orange-300 text-sm"
          data-testid="motion-back-btn">← Back</button>
        <div>
          <p className="font-mono text-[10px] tracking-[0.2em] text-orange-400">STAGE 13.5 · MOTION</p>
          <h1 className="text-xl font-extrabold tracking-tight">MOTION ROOM</h1>
        </div>
        <span className="ml-auto font-mono text-[10px] text-white/30 tracking-widest">
          M1 · FABLE 5 + REMOTION SANDBOX
        </span>
      </header>

      {step === "brief" && (
        <div className="max-w-2xl space-y-4" data-testid="motion-brief-view">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-white/40 mb-1.5">Project</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
              data-testid="motion-project-select"
              className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-500">
              <option value="">Select a project…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}
                  {p.brand_tag ? ` · ${p.brand_tag}` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-white/40 mb-1.5">Brief</label>
            <textarea value={brief} onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. ZURA end card · &quot;Drink the Edge.&quot; CTA · brand-red BG, white headline, fade in 0.5s hold 2s fade out"
              data-testid="motion-brief-input"
              className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-500 min-h-[120px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-white/40 mb-1.5">Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)}
                data-testid="motion-type-select"
                className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none">
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-white/40 mb-1.5">Orientation</label>
              <select value={orientation} onChange={(e) => setOrientation(e.target.value)}
                data-testid="motion-orientation-select"
                className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none">
                <option value="16:9">16:9</option>
                <option value="9:16">9:16</option>
                <option value="1:1">1:1</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-white/40 mb-1.5">Duration (s)</label>
              <input type="number" min="0.5" max="60" step="0.5" value={duration}
                onChange={(e) => setDuration(parseFloat(e.target.value || 3))}
                data-testid="motion-duration-input"
                className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-white/40 mb-1.5">Brand tag (optional)</label>
              <input type="text" value={brandTag}
                onChange={(e) => setBrandTag(e.target.value)}
                placeholder="ZURA"
                data-testid="motion-brand-input"
                className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none" />
            </div>
          </div>
          <button onClick={generate} disabled={busy === "generate" || !projectId}
            data-testid="motion-generate-btn"
            className="px-5 py-2 rounded-lg text-sm font-bold bg-orange-500 text-black hover:bg-orange-400 disabled:opacity-40">
            {busy === "generate" ? "Generating…" : "Generate composition"}
          </button>
          <p className="text-[10px] font-mono text-white/30">
            Motion Room runs <code className="text-orange-300">{`{MOTION_MODEL}`}</code>
            via Universal Key (default <code className="text-orange-300">claude-opus-4-8</code>).
            Render path lights up the moment Rohit&apos;s EC2 worker
            consumes Remotion jobs.
          </p>
        </div>
      )}

      {step === "preview" && mograph && (
        <div className="grid grid-cols-12 gap-6" data-testid="motion-preview-view">
          <main className="col-span-8 space-y-4">
            <div className="aspect-video bg-black rounded-lg overflow-hidden border border-white/10 grid place-items-center">
              <div className="text-center px-8">
                <p className="font-mono text-[10px] tracking-widest text-orange-400 mb-2">
                  REMOTION PLAYER · MOUNT POINT
                </p>
                <p className="text-sm text-white/40">
                  Live preview lands when `@remotion/player` ships in the next
                  slice. Until then the param panel below + the render queue
                  are the testable surfaces.
                </p>
                <p className="font-mono text-[10px] text-white/30 mt-3">
                  asset id · {mograph.id.slice(0, 8)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={renderAsset} disabled={busy === "render"}
                data-testid="motion-render-btn"
                className="px-4 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40">
                {busy === "render" ? "Queuing…" : "Render on EC2"}
              </button>
              <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
                STATUS_CHIP[mograph.status] || "bg-white/10 text-white/40"}`}
                data-testid="motion-status-chip">
                {mograph.status} · v{mograph.version}
              </span>
              {mograph.codegen_stub && (
                <span className="text-[10px] font-mono text-amber-300">
                  · stub TSX (codegen wiring pending)
                </span>
              )}
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-white/40 hover:text-orange-300">
                View generated TSX (read-only — sandbox-validated)
              </summary>
              <pre className="mt-2 p-3 bg-black/40 rounded text-[11px] overflow-x-auto text-white/70">
                {mograph.tsx}
              </pre>
            </details>
          </main>
          <aside className="col-span-4 max-h-[80vh] overflow-y-auto pr-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-white/30 mb-3">
              Params · {sortedProps.length}
            </p>
            <div className="space-y-3">
              {sortedProps.map(([key, schema]) => (
                <div key={key}>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1">{key}</label>
                  {schema.type === "string" && key.toLowerCase().includes("color") ? (
                    <input type="color"
                      value={paramsValues[key] || schema.default || "#ffffff"}
                      onChange={(e) => {
                        const next = { ...paramsValues, [key]: e.target.value };
                        setParamsValues(next);
                        saveParams(next);
                      }}
                      data-testid={`motion-param-${key}`}
                      className="w-full h-9 rounded border border-white/15 bg-transparent" />
                  ) : (
                    <input type="text"
                      value={paramsValues[key] || ""}
                      placeholder={schema.default || ""}
                      onChange={(e) => {
                        const next = { ...paramsValues, [key]: e.target.value };
                        setParamsValues(next);
                      }}
                      onBlur={(e) => saveParams({ ...paramsValues, [key]: e.target.value })}
                      data-testid={`motion-param-${key}`}
                      className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-orange-500" />
                  )}
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
